import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Alert, Box, Button, Card, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, LinearProgress, MenuItem, Snackbar, Stack, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  Typography
} from '@mui/material';
import {
  AccountBalanceWallet as PayrollIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import AppHeader from '../../components/AppHeader';

const reasons = [
  ['INITIAL_SETUP', 'Initial payroll setup'],
  ['PROMOTION', 'Promotion'],
  ['TRANSFER', 'Transfer'],
  ['INCREMENT', 'Salary increment'],
  ['DECREMENT', 'Salary decrease'],
  ['CORRECTION', 'Correction']
];

const toDateInput = (value) => {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) return String(value);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};
const money = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });

const PendingPayrollSetups = ({ setIsLoggedIn }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [bases, setBases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [message, setMessage] = useState({ open: false, severity: 'success', text: '' });
  const [form, setForm] = useState({
    basic_salary: '', calculation_basis_id: '', payroll_effective_from: '', payroll_change_reason: 'INITIAL_SETUP'
  });

  const requestConfig = useCallback(() => ({
    withCredentials: true,
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
      'X-Tenant': localStorage.getItem('tenant') || ''
    }
  }), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pendingResponse, basisResponse] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll/pending-assignments`, requestConfig()),
        axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll/calc-bases`, requestConfig())
      ]);
      setRows(pendingResponse.data?.data || []);
      setBases((basisResponse.data?.data || []).filter((basis) => Number(basis.is_active) === 1));
    } catch (error) {
      setMessage({ open: true, severity: 'error', text: error.response?.data?.message || 'Could not load pending payroll setups' });
    } finally {
      setLoading(false);
    }
  }, [requestConfig]);

  useEffect(() => { load(); }, [load]);

  const openSetup = (row) => {
    setSelected(row);
    setForm({
      basic_salary: row.assignment_salary ?? '',
      calculation_basis_id: bases[0]?.id || '',
      payroll_effective_from: toDateInput(row.assignment_effective_from),
      payroll_change_reason: row.assignment_reason === 'PROMOTION' || row.assignment_reason === 'TRANSFER'
        ? row.assignment_reason : 'INITIAL_SETUP'
    });
  };

  const submit = async () => {
    if (!form.basic_salary || !form.calculation_basis_id || !form.payroll_effective_from) {
      setMessage({ open: true, severity: 'warning', text: 'Salary, calculation basis, and effective date are required' });
      return;
    }
    if (Number(form.basic_salary) <= 0) {
      setMessage({ open: true, severity: 'warning', text: 'Basic salary must be greater than 0' });
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL1}/sm/payroll/assignments/${selected.assignment_id}/complete`,
        { ...form, basic_salary: Number(form.basic_salary), calculation_basis_id: Number(form.calculation_basis_id) },
        requestConfig()
      );
      setSelected(null);
      setMessage({ open: true, severity: 'success', text: 'Payroll setup completed and compensation timeline updated' });
      await load();
    } catch (error) {
      setMessage({ open: true, severity: 'error', text: error.response?.data?.message || 'Could not complete payroll setup' });
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f4f7fe' }}>
      <AppHeader title="Pending Payroll Setups" setIsLoggedIn={setIsLoggedIn} />
      {loading && <LinearProgress />}
      <Box sx={{ p: 3 }}>
        <Card sx={{ p: 3, mb: 2, borderRadius: 3 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <PayrollIcon color="primary" />
                <Typography variant="h6" fontWeight={800}>Assignments waiting for payroll</Typography>
                <Chip size="small" color={rows.length ? 'warning' : 'success'} label={rows.length} />
              </Stack>
              <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                Employment changes remain pending until an authorized payroll user supplies salary and its effective date.
              </Typography>
            </Box>
            <Button startIcon={<RefreshIcon />} variant="outlined" onClick={load}>Refresh</Button>
          </Stack>
        </Card>

        {!loading && rows.length === 0 ? (
          <Alert severity="success">There are no assignments waiting for payroll setup.</Alert>
        ) : (
          <TableContainer component={Card} sx={{ borderRadius: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Staff</TableCell><TableCell>New assignment</TableCell>
                  <TableCell>Effective date</TableCell><TableCell>Employment reason</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.assignment_id} hover>
                    <TableCell>
                      <Typography fontWeight={700}>{row.full_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{row.staff_code} · Employee {row.employee_number || '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography>{row.job_title || '-'}</Typography>
                      <Typography variant="caption" color="text.secondary">{[row.organization_name, row.branch_name, row.grade_name].filter(Boolean).join(' · ')}</Typography>
                    </TableCell>
                    <TableCell>{toDateInput(row.assignment_effective_from)}</TableCell>
                    <TableCell><Chip size="small" label={row.assignment_reason || 'NEW_ASSIGNMENT'} /></TableCell>
                    <TableCell align="right">
                      <Stack direction="row" justifyContent="flex-end" spacing={1}>
                        <Button size="small" onClick={() => navigate(`/staff/${row.staff_id}`)}>View staff</Button>
                        <Button size="small" variant="contained" onClick={() => openSetup(row)}>Enter payroll</Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Dialog open={Boolean(selected)} onClose={() => !loading && setSelected(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Complete payroll setup</DialogTitle>
        <DialogContent>
          {selected && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="info">
                {selected.full_name} — assignment effective {toDateInput(selected.assignment_effective_from)}. This creates or updates the effective-dated compensation record; it does not recreate the assignment.
              </Alert>
              <TextField required label="Basic salary" type="number" value={form.basic_salary}
                helperText={form.basic_salary ? `${money.format(Number(form.basic_salary))} MMK` : ''}
                onChange={(e) => setForm({ ...form, basic_salary: e.target.value })} />
              <TextField required select label="Calculation basis" value={form.calculation_basis_id}
                onChange={(e) => setForm({ ...form, calculation_basis_id: e.target.value })}>
                {bases.map((basis) => <MenuItem key={basis.id} value={basis.id}>{basis.basis_name} ({basis.basis_code})</MenuItem>)}
              </TextField>
              <TextField required label="Payroll effective date" type="date" InputLabelProps={{ shrink: true }}
                value={form.payroll_effective_from}
                onChange={(e) => setForm({ ...form, payroll_effective_from: e.target.value })} />
              <TextField required select label="Payroll change reason" value={form.payroll_change_reason}
                onChange={(e) => setForm({ ...form, payroll_change_reason: e.target.value })}>
                {reasons.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
              </TextField>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)} disabled={loading}>Cancel</Button>
          <Button variant="contained" onClick={submit} disabled={loading}>Complete payroll setup</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={message.open} autoHideDuration={5000} onClose={() => setMessage({ ...message, open: false })}>
        <Alert severity={message.severity} onClose={() => setMessage({ ...message, open: false })}>{message.text}</Alert>
      </Snackbar>
    </Box>
  );
};

export default PendingPayrollSetups;
